import { Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  MESSAGE_MAX_LENGTH,
  createSupportTicket,
  supportErrorMessage,
  validateSupportTicket,
} from '../../helpers/supportApi';

export default function SupportPage() {
  const [message, setMessage] = useState('');
  const [loomUrl, setLoomUrl] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const thanksRef = useRef(null);

  useEffect(() => {
    if (ticket) thanksRef.current?.focus();
  }, [ticket]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const validation = validateSupportTicket({ message, loomUrl });
    if (!validation.ok) {
      setFieldErrors(validation.errors);
      setSubmitError('');
      return;
    }

    setFieldErrors({});
    setSubmitError('');
    setSubmitting(true);
    try {
      const created = await createSupportTicket(validation.body);
      setTicket(created);
    } catch (requestError) {
      setSubmitError(supportErrorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  const submitAnother = () => {
    setTicket(null);
    setMessage('');
    setLoomUrl('');
    setFieldErrors({});
    setSubmitError('');
  };

  const messageErrorId = fieldErrors.message ? 'support-message-error' : undefined;
  const loomDescribedBy = [
    'support-loom-help',
    fieldErrors.loomUrl ? 'support-loom-error' : null,
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Help & support</h1>
          <div className="page-subtitle">Get help with hosted sites, billing, and domain setup.</div>
        </div>
      </div>

      <div className="support-grid">
        {ticket ? (
          <section className="panel-card card-pad" role="status">
            <h2 ref={thanksRef} tabIndex={-1}>Thanks, we got your request</h2>
            <p>Your ticket number is #{ticket.ticket_number}.</p>
            <button className="btn-text" type="button" onClick={submitAnother}>
              Submit another request
            </button>
          </section>
        ) : (
          <section className="panel-card card-pad">
            <h2>Submit a request</h2>
            <p>We&apos;ll include your account details (name, email, sites, and plan) so we can help faster.</p>
            <form className="support-form" onSubmit={onSubmit} noValidate>
              {submitError ? <div className="error-banner" role="alert">{submitError}</div> : null}
              <div className="field-stack">
                <div className="form-field">
                  <div className="support-label-row">
                    <label htmlFor="support-message">How can we help?</label>
                    <span
                      className={`support-count${message.length > MESSAGE_MAX_LENGTH ? ' is-over' : ''}`}
                      id="support-message-count"
                    >
                      {message.length} / {MESSAGE_MAX_LENGTH}
                    </span>
                  </div>
                  <textarea
                    id="support-message"
                    name="message"
                    rows={6}
                    placeholder="Describe what happened, which site it affects, and what you expected."
                    value={message}
                    aria-invalid={fieldErrors.message ? 'true' : undefined}
                    aria-describedby={messageErrorId}
                    disabled={submitting}
                    onChange={(event) => {
                      setMessage(event.target.value);
                      if (fieldErrors.message) {
                        setFieldErrors((current) => ({ ...current, message: undefined }));
                      }
                    }}
                  />
                  {fieldErrors.message ? (
                    <span className="field-error" id="support-message-error">{fieldErrors.message}</span>
                  ) : null}
                </div>
                <div className="form-field">
                  <label htmlFor="support-loom">Loom video (optional)</label>
                  <input
                    id="support-loom"
                    name="loom_url"
                    type="url"
                    inputMode="url"
                    placeholder="https://www.loom.com/share/…"
                    value={loomUrl}
                    aria-invalid={fieldErrors.loomUrl ? 'true' : undefined}
                    aria-describedby={loomDescribedBy}
                    disabled={submitting}
                    onChange={(event) => {
                      setLoomUrl(event.target.value);
                      if (fieldErrors.loomUrl) {
                        setFieldErrors((current) => ({ ...current, loomUrl: undefined }));
                      }
                    }}
                  />
                  <span className="hint" id="support-loom-help">
                    Paste a Loom share link if a quick recording helps explain it.
                  </span>
                  {fieldErrors.loomUrl ? (
                    <span className="field-error" id="support-loom-error">{fieldErrors.loomUrl}</span>
                  ) : null}
                </div>
              </div>
              <div className="support-actions">
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  <Send size={15} />
                  {submitting ? 'Sending…' : 'Submit request'}
                </button>
                <p className="support-fallback">
                  Or email <a href="mailto:hello@northcommerce.com">hello@northcommerce.com</a>
                </p>
              </div>
            </form>
          </section>
        )}

        <section className="panel-card card-pad">
          <h2>Documentation</h2>
          <p>Review setup docs for staging sites, custom domains, DNS, and billing.</p>
          <a className="btn btn-ghost" href="/tutorials">Open tutorials</a>
        </section>
      </div>
    </>
  );
}
